import { useState } from 'react';
import { ChevronDown, GripVertical } from 'lucide-react';
import type { FilaDeCartera } from '@/api/cartera.api';
import { repartirEnGrupos } from '@/components/datos/tabla-pura';
import { Avatar } from '@/components/ui/Avatar';
import { cn } from '@/lib/cn';
import { PIPELINE_COLUMNS } from '@/lib/crm';
import { CERRADO, datosDe, plazoDe, posicionDe, type Plazo } from '@/lib/proceso';
import { alSoltar, gruposDeEtapa, sumaDePresupuestos, type AlSoltar, type GrupoDeClientes } from '@/lib/tablero-clientes';

/**
 * EL KANBAN DE «CLIENTES» (tanda 6): una columna por paso del proceso, con el color de su grupo
 * (los mismos de la tabla), y cada cliente en una tarjeta que se arrastra a otra columna para
 * cambiarle la etapa.
 *
 * Arrastre nativo del navegador, sin librerías (como `pages/crm/Crm.tsx`). En el teléfono no hay
 * arrastre: cada tarjeta trae «Mover a…», que es también el camino con el teclado. Qué pasa al
 * soltar lo decide `alSoltar` (lib/tablero-clientes); guardar, confirmar el cierre y volver atrás
 * si el servidor no quiere es de quien recibe `onMover` (`useEdicionDeClientes`).
 */
export interface TableroPorEtapaProps {
  /** Ya filtradas, buscadas y ordenadas por la pantalla. */
  filas: FilaDeCartera[];
  etapas: readonly string[];
  ahora: Date;
  onAbrir: (fila: FilaDeCartera) => void;
  /** La tarjeta se soltó en esa columna, o se eligió en su «Mover a…» (`destino`: la clave del paso). */
  onMover: (fila: FilaDeCartera, destino: string) => void;
}

/** El tipo propio del arrastre: una columna solo recibe tarjetas de este tablero, no un archivo ni un texto cualquiera. */
const TIPO_DE_ARRASTRE = 'application/x-conversia-cliente';

const TONO_DE_PLAZO: Record<Plazo['tono'], string> = {
  normal: 'text-slate-600 dark:text-slate-300',
  alerta: 'font-medium text-amber-700 dark:text-amber-400',
  apagado: 'text-slate-500 dark:text-slate-400',
};

/** Una tarjeta del kanban: compacta y clara. Sin estado: se prueba con renderToStaticMarkup. */
export function TarjetaDeKanban({
  fila,
  pasos,
  actual,
  ahora,
  enElAire = false,
  onAbrir,
  onMover,
  onArrastrar,
  onDejar,
}: {
  fila: FilaDeCartera;
  /** Los pasos del proceso, para «Mover a…». */
  pasos: GrupoDeClientes[];
  /** La clave del paso (la columna) en que está. */
  actual: string;
  ahora: Date;
  /** La están arrastrando: se apaga en su sitio hasta que la suelten. */
  enElAire?: boolean;
  onAbrir?: () => void;
  onMover?: (destino: string) => void;
  onArrastrar?: () => void;
  onDejar?: () => void;
}) {
  // Uno ya cerrado no se mueve de etapa desde aquí: reabrirlo es cambiar su temperatura en la ficha.
  const cerrada = actual === CERRADO;
  const temperatura = PIPELINE_COLUMNS.find((c) => c.status === fila.conversacion?.temperatura);
  const nombreDeTemperatura = temperatura?.label ?? 'Sin temperatura';
  const datos = datosDe(fila);
  const plazo = plazoDe(fila, ahora);
  return (
    <article
      draggable={!cerrada}
      data-cliente={fila.contactId}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData(TIPO_DE_ARRASTRE, fila.contactId);
        onArrastrar?.();
      }}
      onDragEnd={onDejar}
      // El nombre es el botón (para el teclado y el lector de pantalla); con el dedo o el cursor vale toda la tarjeta.
      onClick={onAbrir}
      className={cn(
        'group cursor-pointer select-none rounded-lg border border-surface-border bg-surface p-2.5 shadow-sm',
        'transition-[transform,box-shadow,opacity] duration-200 ease-out hover:shadow-md motion-safe:hover:-translate-y-0.5 dark:bg-slate-800',
        enElAire && 'opacity-40',
      )}
    >
      <div className="flex items-start gap-2.5">
        {/* La foto no se arrastra sola: lo que se lleva el cursor es la tarjeta entera. */}
        <span className="relative mt-0.5 shrink-0 [&_img]:pointer-events-none">
          <Avatar name={fila.nombre} src={fila.avatarUrl} size="sm" />
          <span title={nombreDeTemperatura} className={cn('absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-surface dark:ring-slate-800', temperatura?.dot ?? 'bg-slate-300 dark:bg-slate-600')}>
            <span className="sr-only">{nombreDeTemperatura}</span>
          </span>
        </span>
        <div className="min-w-0 flex-1">
          <button
            type="button"
            title={`Ver la ficha de ${fila.nombre}`}
            className={
              'block max-w-full truncate rounded text-left text-sm font-semibold leading-snug text-slate-900 group-hover:text-brand-700 ' +
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 dark:text-slate-100 dark:group-hover:text-brand-300'
            }
          >
            {fila.nombre}
          </button>
          {datos.length > 0 && <p className="truncate text-xs leading-snug text-slate-500 dark:text-slate-400">{datos.join(' · ')}</p>}
        </div>
        {!cerrada && <GripVertical aria-hidden className="hidden h-4 w-4 shrink-0 cursor-grab text-slate-300 transition-colors group-hover:text-slate-500 md:block dark:text-slate-600" />}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center justify-between gap-x-2 md:mt-2">
        {cerrada ? (
          <p className="text-xs text-slate-500 dark:text-slate-400">Se reabre desde su ficha</p>
        ) : (
          <>
            {/* El plazo va en un solo renglón (regla del 21-oct): si no cabe junto a «Mover a…», ese baja. */}
            <p data-tono={plazo.tono} title={plazo.texto} className={cn('min-w-0 truncate text-xs leading-4', TONO_DE_PLAZO[plazo.tono])}>
              {plazo.texto}
            </p>
            {/* Un `<select>` de verdad, invisible, encima del rótulo: el teléfono abre su selector nativo y lo que se toca mide 44 px. */}
            <span className="relative inline-flex min-h-[44px] shrink-0 items-center md:min-h-0" onClick={(e) => e.stopPropagation()}>
              <select
                aria-label={`Mover a ${fila.nombre} a otra etapa`}
                value=""
                onChange={(e) => {
                  const destino = e.target.value;
                  // Vuelve a su rótulo: si el cambio se cancela o falla, no queda marcando lo que no es.
                  e.target.value = '';
                  if (destino) onMover?.(destino);
                }}
                className="peer absolute inset-0 h-full w-full cursor-pointer appearance-none text-base opacity-0"
              >
                <option value="" disabled hidden>
                  Mover a…
                </option>
                {pasos
                  .filter((p) => p.clave !== actual)
                  .map((p) => (
                    <option key={p.clave} value={p.clave}>
                      {p.etiqueta}
                    </option>
                  ))}
              </select>
              <span
                aria-hidden
                className={
                  'pointer-events-none inline-flex h-7 items-center gap-0.5 rounded-md border border-surface-border pl-2 pr-1 text-xs font-medium text-slate-600 ' +
                  'transition-colors peer-hover:border-slate-300 peer-hover:bg-slate-100 peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 ' +
                  'dark:border-slate-600 dark:text-slate-300 dark:peer-hover:bg-slate-700'
                }
              >
                Mover a…
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </span>
            </span>
          </>
        )}
      </div>
    </article>
  );
}

/**
 * Una columna: encabezado con el color del paso, cuántos hay y cuánto suman sus presupuestos, y
 * debajo sus tarjetas. `veredicto` es lo que dice `alSoltar` de la tarjeta que se está arrastrando
 * (null si no hay ninguna en el aire); con ella `encima`, la columna se enciende si la acepta y
 * dice por qué no si la rechaza. Sin estado: se prueba con renderToStaticMarkup.
 */
export function ColumnaDeEtapa({
  paso,
  pasos,
  filas,
  ahora,
  veredicto = null,
  encima = false,
  enElAire = null,
  onAbrir,
  onMover,
  onArrastrar,
  onDejar,
  onEncima,
  onFuera,
  onSoltar,
}: {
  paso: GrupoDeClientes;
  pasos: GrupoDeClientes[];
  filas: FilaDeCartera[];
  ahora: Date;
  veredicto?: AlSoltar | null;
  encima?: boolean;
  /** El id de la tarjeta que se está arrastrando. */
  enElAire?: string | null;
  onAbrir?: (fila: FilaDeCartera) => void;
  onMover?: (fila: FilaDeCartera, destino: string) => void;
  onArrastrar?: (fila: FilaDeCartera) => void;
  onDejar?: () => void;
  onEncima?: () => void;
  onFuera?: () => void;
  /** Con el id que viaja en el arrastre (vacío si el navegador no lo dejó leer). */
  onSoltar?: (contactId: string) => void;
}) {
  const acepta = veredicto?.tipo === 'mover' || veredicto?.tipo === 'confirmar';
  const rechazo = encima && veredicto?.tipo === 'no' ? veredicto.porque : null;
  const suma = sumaDePresupuestos(filas);
  return (
    <section
      aria-label={paso.etiqueta}
      data-columna={paso.clave}
      data-soltar={encima && acepta ? 'si' : rechazo ? 'no' : undefined}
      onDragOver={(e) => {
        // Solo las tarjetas de este tablero: un archivo o un texto arrastrado desde fuera no es asunto suyo.
        if (!Array.from(e.dataTransfer.types).includes(TIPO_DE_ARRASTRE)) return;
        // Sin `preventDefault` el navegador no deja soltar: es lo que se quiere donde no se puede.
        if (acepta) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
        }
        onEncima?.();
      }}
      onDragLeave={(e) => {
        // Pasar por encima de una tarjeta de la columna no es salir de la columna.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onFuera?.();
      }}
      onDrop={(e) => {
        e.preventDefault();
        onSoltar?.(e.dataTransfer.getData(TIPO_DE_ARRASTRE));
      }}
      className={cn(
        'flex w-72 shrink-0 snap-start flex-col overflow-hidden rounded-xl border transition-[background-color,border-color,box-shadow] duration-150',
        // Con muchos clientes en un paso, se desplaza la columna y no la página: los encabezados siguen a la vista para soltar.
        'md:max-h-[max(24rem,calc(100dvh-17rem-var(--alto-de-pestanas,0px)))]',
        encima && acepta
          ? 'border-brand-400 bg-brand-50 ring-2 ring-brand-400/40 dark:bg-brand-900/20'
          : rechazo
            ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20'
            : 'border-surface-border bg-slate-100/80 dark:bg-slate-900/60',
      )}
    >
      <header className={cn('flex items-center gap-2 px-3 py-2 text-white', paso.color)}>
        <h3 className="min-w-0 truncate text-sm font-semibold">{paso.etiqueta}</h3>
        <span className="shrink-0 rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold tabular-nums" title={filas.length === 1 ? '1 cliente' : `${filas.length} clientes`}>
          {filas.length}
        </span>
        {suma && (
          <span title="Cuánto suman sus presupuestos" className="ml-auto shrink-0 text-xs font-semibold tabular-nums">
            {suma}
          </span>
        )}
      </header>
      {rechazo && (
        <p role="status" className="border-b border-amber-300 px-3 py-2 text-xs font-medium leading-snug text-amber-900 dark:border-amber-700 dark:text-amber-200">
          {rechazo}
        </p>
      )}
      <ul className="min-h-0 flex-1 space-y-2 p-2 md:overflow-y-auto">
        {filas.length === 0 ? (
          <li className="rounded-lg border border-dashed border-slate-300 px-2 py-5 text-center text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
            {encima && acepta ? 'Suéltalo aquí' : 'Nadie en esta etapa.'}
          </li>
        ) : (
          filas.map((f) => (
            <li key={f.contactId}>
              <TarjetaDeKanban
                fila={f}
                pasos={pasos}
                actual={paso.clave}
                ahora={ahora}
                enElAire={f.contactId === enElAire}
                onAbrir={onAbrir && (() => onAbrir(f))}
                onMover={onMover && ((destino) => onMover(f, destino))}
                onArrastrar={onArrastrar && (() => onArrastrar(f))}
                onDejar={onDejar}
              />
            </li>
          ))
        )}
      </ul>
    </section>
  );
}

/** El tablero entero. Solo recuerda qué tarjeta va en el aire y sobre qué columna está. */
export function TableroPorEtapa({ filas, etapas, ahora, onAbrir, onMover }: TableroPorEtapaProps) {
  const [enElAire, setEnElAire] = useState<string | null>(null);
  const [sobre, setSobre] = useState<string | null>(null);
  const pasos = gruposDeEtapa(etapas);
  const columnas = repartirEnGrupos(filas, { grupos: pasos, de: (f) => pasos[posicionDe(f, etapas)]!.clave, mostrarVacios: true });
  const arrastrada = enElAire ? (filas.find((f) => f.contactId === enElAire) ?? null) : null;
  const dejar = () => {
    setEnElAire(null);
    setSobre(null);
  };
  return (
    // En el teléfono se desplaza de lado y cada columna se acomoda al borde; con mouse, sin imán.
    <div className="-mx-4 flex snap-x scroll-px-4 gap-3 overflow-x-auto px-4 pb-3 md:mx-0 md:snap-none md:px-0">
      {columnas.map((c) => (
        <ColumnaDeEtapa
          key={c.clave}
          paso={pasos.find((p) => p.clave === c.clave)!}
          pasos={pasos}
          filas={c.filas}
          ahora={ahora}
          veredicto={arrastrada && alSoltar(arrastrada, c.clave, etapas)}
          encima={sobre === c.clave}
          // La tarjeta se apaga en su sitio cuando ya va por encima de una columna, no al agarrarla:
          // el navegador le toma la foto que sigue al cursor justo después, y saldría apagada también.
          enElAire={sobre ? enElAire : null}
          onAbrir={onAbrir}
          onMover={onMover}
          onArrastrar={(f) => setEnElAire(f.contactId)}
          onDejar={dejar}
          onEncima={() => setSobre(c.clave)}
          onFuera={() => setSobre((s) => (s === c.clave ? null : s))}
          onSoltar={(contactId) => {
            const fila = filas.find((f) => f.contactId === (contactId || enElAire));
            dejar();
            if (fila) onMover(fila, c.clave);
          }}
        />
      ))}
    </div>
  );
}
